import { TGlobalStores } from '@/editor/stores';
import { EDirection, EOperateMode, ESceneMode, EToolType } from '@/editor/types';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ECcontroller3dMouseEvent } from './type';
import { MOUSE } from 'three';
import { pixelRatio3d } from '@/editor/math/const';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { MouseCoordinateConverter } from '@/editor/utils/coordinatetransform';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { AbstractCommonController } from '../AbstractCommonController';
import * as THREE from 'three';
import { createKeyboardMoveUpdate } from '../utils';
import { expandByWallOutlines } from '@/editor/utils/scene';

const MIN_ZOOM_LEVEL = 10;
const MAX_ZOOM_LEVEL = 10000;

export class CommonController3d extends OrbitControls implements AbstractCommonController {
  protected _domElement: HTMLElement;
  protected _converter: MouseCoordinateConverter;
  protected _keyboardMoveUpdate: (() => void) | null = null;
  protected _exitOperateMode: EOperateMode = EOperateMode.EMPTY;

  private _initialDistance: number;

  constructor(protected _gs: TGlobalStores) {
    const camera = _gs.editorStateStore.sceneInfo.scene3d.camera;
    const canvasElement = _gs.editorStateStore.sceneInfo.canvasElement;
    const target = camera.userData.target;

    super(camera, canvasElement);

    this._domElement = canvasElement;

    this.mouseButtons = {
      LEFT: MOUSE.ROTATE, // 左键旋转
      MIDDLE: MOUSE.PAN, // 中键平移
      RIGHT: MOUSE.PAN // 右键平移
    };

    this.enableRotate = true;
    this.screenSpacePanning = false;
    this.minZoom = MIN_ZOOM_LEVEL / 100;
    this.maxZoom = MAX_ZOOM_LEVEL / 100;
    this.enableDamping = false; // 启用阻尼效果（惯性）
    this.dampingFactor = 0.05; // 阻尼系数
    this.zoomToCursor = true;

    this._converter = new MouseCoordinateConverter({
      camera: this._gs.editorStateStore.sceneInfo.scene3d.camera,
      scene: this._gs.editorStateStore.sceneInfo.scene3d.scene,
      intersectableGroup: this._gs.editorStateStore.sceneInfo.scene3d.intersectableGroup,
      canvasElement: this._gs.editorStateStore.sceneInfo.canvasElement,
      renderer: this._gs.editorStateStore.sceneInfo.renderer
    });

    this.target.copy(target);
    this._initialDistance = this.getDistance() * (this.getValidZoomLevel(this._gs.scene3dZoom) / 100);
    this.updateDistanceLimits();
    this._keyboardMoveUpdate = createKeyboardMoveUpdate(ESceneMode.scene3d, this.target);
    this.update();
  }

  public keyboardMoveUpdate() {
    if (this._keyboardMoveUpdate) {
      this._keyboardMoveUpdate();
      this.update();
    }
  }

  public moveEntityByDirection(_direction: EDirection) {}

  public flushSync() {}

  init() {
    this._domElement.addEventListener(ECcontroller3dMouseEvent.mousedown, this.commonMouseDown);
    this._domElement.addEventListener(ECcontroller3dMouseEvent.mousemove, this.commonMouseMove);
    this._domElement.addEventListener(ECcontroller3dMouseEvent.mouseup, this.commonMouseUp);
    this.addEventListener('change', this.handleViewChange);

    editorEvent.on(EditorEventName.ZOOM_UPDATE_3D, this.updateZoomLevel);
    editorEvent.on(EditorEventName.RESIZE, this.handleResize);
    this.handleResize();
  }

  get zoomLevel() {
    const distance = this.getDistance();
    const zoom = this._initialDistance / distance; // 计算缩放比例

    return zoom * 100;
  }

  get exitOperateMode() {
    return this._exitOperateMode;
  }

  set exitOperateMode(mode: EOperateMode) {
    this._exitOperateMode = mode;
  }

  protected getPointOnPlane(event: MouseEvent): IPoint2d | null {
    const clickedPoint = this._converter.getPointOnPlane(event, this._gs.editorStateStore.sceneInfo.canvasElement);
    if (!clickedPoint) return null;

    return {
      x: clickedPoint.x * pixelRatio3d,
      y: clickedPoint.y * pixelRatio3d
    };
  }

  protected mouseDown(_event: MouseEvent): void {}
  protected mouseMove(_event: MouseEvent): void {}
  protected mouseUp(_event: MouseEvent): void {}

  private commonMouseDown = (event: MouseEvent) => {
    this.mouseDown(event);
  };

  private commonMouseMove = (event: MouseEvent) => {
    this.mouseMove(event);
  };

  private commonMouseUp = (event: MouseEvent) => {
    this.mouseUp(event);
  };

  private handleViewChange = (_e?: THREE.Event<'change', this>) => {
    this.updateGlobal3dZoom();
    editorEvent.emit(EditorEventName.VIEW3D_CHANGE);
    this._gs.editorStateStore.setZooming();
  };

  private updateGlobal3dZoom() {
    this._gs.scene3dZoom = this.zoomLevel;
  }

  private getDistanceByZoomLevel(zoom: number) {
    return this._initialDistance / (zoom / 100);
  }

  private updateDistanceLimits() {
    this.minDistance = this.getDistanceByZoomLevel(MAX_ZOOM_LEVEL);
    this.maxDistance = this.getDistanceByZoomLevel(MIN_ZOOM_LEVEL);
  }

  private normalizeZoomLevel(zoom: number) {
    return THREE.MathUtils.clamp(zoom, MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL);
  }

  private getValidZoomLevel(zoom: number) {
    if (!Number.isFinite(zoom) || zoom <= 0) return 100;

    return this.normalizeZoomLevel(zoom);
  }

  private updateZoomLevel = (zoom: number) => {
    if (Number.isFinite(zoom) && zoom > 0) {
      const nextZoom = this.getValidZoomLevel(zoom);
      const zoomClamped = Math.abs(nextZoom - zoom) > 0.001;

      if (Math.abs(nextZoom - this.zoomLevel) <= 0.001) {
        if (zoomClamped) this._gs.scene3dZoom = nextZoom;
        return;
      }

      const camera = this._gs.editorStateStore.sceneInfo.scene3d.camera;
      const newDistance = this.getDistanceByZoomLevel(nextZoom);
      const offset = camera.position.clone().sub(this.target);

      camera.position.copy(this.target).add(offset.setLength(newDistance));
      this.update();
      if (zoomClamped) this._gs.scene3dZoom = nextZoom;
    }
  };

  fitToView() {
    const camera = this._gs.editorStateStore.sceneInfo.scene3d.camera;
    const contentBoundingBox = new THREE.Box3();
    const cameraPos = new THREE.Vector3();
    const boxCenter = new THREE.Vector3();

    // 将墙体数据加入边界计算
    expandByWallOutlines(contentBoundingBox);

    if (contentBoundingBox.isEmpty()) {
      cameraPos.set(0, -10, 10);
      boxCenter.set(0, 0, 0);
    } else {
      contentBoundingBox.getCenter(boxCenter);
      const boxSize = contentBoundingBox.getSize(new THREE.Vector3());
      const maxBoxSize = Math.max(boxSize.y, boxSize.z);
      cameraPos.set(boxCenter.x, boxCenter.y - boxSize.y, boxCenter.z + maxBoxSize / 2);
    }

    const resultZoomLv = this._initialDistance / boxCenter.distanceTo(cameraPos);

    // 避免缩放率超过 100%
    if (resultZoomLv > 1) {
      cameraPos.sub(boxCenter).multiplyScalar(resultZoomLv).add(boxCenter);
    }

    // 设置相机位置
    camera.position.copy(cameraPos);
    camera.lookAt(boxCenter);
    this.target.copy(boxCenter);
    this.updateGlobal3dZoom();
  }

  handleResize = () => {
    const {
      renderer,
      canvasElement,
      scene3d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const domWidth = this._domElement.clientWidth;
    const domHeight = this._domElement.clientHeight;

    canvasElement.width = domWidth;
    canvasElement.height = domHeight;
    renderer.setSize(domWidth, domHeight);
    camera.aspect = domWidth / domHeight;
    camera.updateProjectionMatrix();
    this.update();
  };

  override dispose() {
    this._domElement.removeEventListener(ECcontroller3dMouseEvent.mousedown, this.commonMouseDown);
    this._domElement.removeEventListener(ECcontroller3dMouseEvent.mousemove, this.commonMouseMove);
    this._domElement.removeEventListener(ECcontroller3dMouseEvent.mouseup, this.commonMouseUp);
    this.removeEventListener('change', this.handleViewChange);

    editorEvent.off(EditorEventName.ZOOM_UPDATE_3D, this.updateZoomLevel);
    editorEvent.off(EditorEventName.RESIZE, this.handleResize);

    const camera = this._gs.editorStateStore.sceneInfo.scene3d.camera;
    camera.userData.target = this.target.clone();

    super.dispose();
  }

  protected exit() {
    this._gs.operateInfo = { mode: this._exitOperateMode, toolType: EToolType.empty };
  }
}
